import { ArrowLeft, KeyRound, Plus } from 'lucide-react';
import {
  Callout,
  DataTable,
  DataTableToolbar,
  DescriptionText,
  Field,
  HorizontalStack,
  RegistryEmptyState,
  SectionHeading,
  Surface,
  TechnicalValue,
  VerticalStack,
  WizardWorkspace,
  type ColumnDef,
  type SortingState,
  type WizardTab,
  toast,
} from '@workspace/silk-ui';
import {
  createPromotionCode,
  getCommerceErrorMessage,
  listPromotionCodes,
  type Promotion,
  type PromotionCode,
} from '@/lib/commerce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const inputClass =
  'h-11 w-full rounded-lg border border-border/60 bg-background px-3 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

function formatCodeTimestamp(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString().slice(0, 16).replace('T', ' ');
}

function toDatetimeLocal(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toISOString(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

// ---------------------------------------------------------------------------
// PromotionCodesPanel — codes DataTable shown outside the promotion wizard
// ---------------------------------------------------------------------------

export function PromotionCodesPanel({
  promotion,
  onBack,
  onAddCode,
}: {
  promotion: Promotion;
  onBack: () => void;
  onAddCode: () => void;
}) {
  const [codes, setCodes] = useState<PromotionCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesError, setCodesError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const loadCodes = useCallback(async () => {
    setCodesLoading(true);
    setCodesError(null);
    try {
      const data = await listPromotionCodes(promotion.promotion_id);
      setCodes(data || []);
    } catch (err) {
      setCodesError(getCommerceErrorMessage(err, 'Failed to load promotion codes.'));
    } finally {
      setCodesLoading(false);
    }
  }, [promotion.promotion_id]);

  useEffect(() => {
    void loadCodes();
  }, [loadCodes]);

  const columns = useMemo<ColumnDef<PromotionCode>[]>(() => defineCommerceRegistryColumns([
    {
      id: 'code',
      accessorKey: 'code',
      size: 240,
      header: 'Code',
      cell: ({ row }) => <TechnicalValue>{row.original.code}</TechnicalValue>,
    },
    {
      id: 'status',
      accessorFn: code => code.status || 'active',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={cn(
            'rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-white',
            row.original.status === 'active'
              ? 'bg-emerald-600'
              : row.original.status === 'paused'
                ? 'bg-amber-500'
                : 'bg-rose-600',
          )}
        >
          {row.original.status || 'active'}
        </span>
      ),
    },
    {
      id: 'starts_at',
      accessorFn: code => formatCodeTimestamp(code.starts_at),
      size: 160,
      header: 'Starts At',
      cell: ({ row }) => (
        <span className="text-[11px] font-medium text-foreground/70">
          {formatCodeTimestamp(row.original.starts_at)}
        </span>
      ),
    },
    {
      id: 'ends_at',
      accessorFn: code => formatCodeTimestamp(code.ends_at),
      size: 160,
      header: 'Ends At',
      cell: ({ row }) => (
        <span className="text-[11px] font-medium text-foreground/70">
          {formatCodeTimestamp(row.original.ends_at)}
        </span>
      ),
    },
    {
      id: 'use_limit',
      accessorFn: code => code.use_limit == null ? 'Unlimited' : String(code.use_limit),
      sortingFn: (left, right) => {
        const leftLimit = left.original.use_limit ?? Number.POSITIVE_INFINITY;
        const rightLimit = right.original.use_limit ?? Number.POSITIVE_INFINITY;
        return leftLimit === rightLimit ? 0 : leftLimit < rightLimit ? -1 : 1;
      },
      size: 110,
      header: 'Use Limit',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {row.original.use_limit != null ? row.original.use_limit : 'Unlimited'}
        </span>
      ),
    },
    {
      id: 'used_count',
      accessorFn: code => code.used_count ?? 0,
      size: 90,
      header: 'Used',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.used_count ?? 0}</span>
      ),
    },
  ], { mode: 'client', registry: 'promotion-codes' }), []);

  return (
    <div className="relative z-50 flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden bg-background animate-in fade-in duration-300">
      <header className="shrink-0 border-b border-border/60 bg-muted/5 px-4 py-2 sm:px-6 sm:py-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
            <button
              type="button"
              onClick={onBack}
              className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/60 transition-all hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-base font-black tracking-tight sm:text-xl">
                Redemption Codes: {promotion.name}
              </h1>
              <p className="hidden truncate text-[10px] font-bold text-muted-foreground/80 sm:block">
                Manage redemption codes for this promotion
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onAddCode}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Add Code</span>
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {codesError ? (
          <div className="px-4 pt-4 sm:px-6">
            <Callout variant="error">{codesError}</Callout>
          </div>
        ) : null}

        <DataTable
          columns={columns}
          data={codes}
          globalFilter={searchQuery}
          onGlobalFilterChange={setSearchQuery}
          sorting={sorting}
          onSortingChange={setSorting}
          className="border-none rounded-none"
          paginationClassName="px-0 py-0"
          showPagination={true}
          loading={codesLoading}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search all promotion codes..."
              className="border-b border-border/40 bg-transparent px-0 py-0"
            />
          )}
          emptyState={
            <div className="px-0 py-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                <KeyRound className="h-6 w-6 text-muted-foreground/60" />
              </div>
              <p className="text-sm font-black text-foreground">No redemption codes yet</p>
              <DescriptionText className="mt-1 text-xs">
                Add the first code to allow customers to redeem this promotion.
              </DescriptionText>
            </div>
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PromotionCodeEditorWizard — WizardWorkspace for adding a new code
// ---------------------------------------------------------------------------

type CodeDraftForm = {
  code: string;
  status: string;
  startsAt: string;
  endsAt: string;
  useLimit: string;
  metadataJSON: string;
};

function createEmptyCodeDraft(): CodeDraftForm {
  return { code: '', status: 'active', startsAt: '', endsAt: '', useLimit: '', metadataJSON: '{}' };
}

const codeEditorTabs: WizardTab[] = [
  { id: 'details', label: 'Details', icon: <KeyRound className="h-4 w-4" /> },
];

export function PromotionCodeEditorWizard({
  isOpen,
  onClose,
  promotion,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  promotion: Promotion;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<CodeDraftForm>(createEmptyCodeDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.code.trim()) {
      setError('Code value is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        code: form.code.trim(),
        status: form.status,
      };
      if (form.startsAt) payload.starts_at = toISOString(form.startsAt);
      if (form.endsAt) payload.ends_at = toISOString(form.endsAt);
      if (form.useLimit.trim()) {
        const parsed = Number.parseInt(form.useLimit, 10);
        if (!Number.isNaN(parsed)) payload.use_limit = parsed;
      }
      try {
        payload.metadata = JSON.parse(form.metadataJSON || '{}');
      } catch {
        payload.metadata = {};
      }
      await createPromotionCode(promotion.promotion_id, payload);
      toast.success('Redemption code added', { description: form.code.trim() });
      onSaved();
    } catch (err) {
      const message = getCommerceErrorMessage(err, 'Failed to create promotion code.');
      setError(message);
      toast.error('Save failed', { description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardWorkspace
      isOpen={isOpen}
      isSaving={saving}
      onClose={onClose}
      title={`Add Code: ${promotion.name}`}
      subtitle="Create a new redemption code for this promotion"
      tabs={codeEditorTabs}
      activeTabId="details"
      onTabChange={() => {}}
      saveLabel="Add Code"
      onSave={() => void handleSave()}
      discardLabel="Back to Codes"
    >
      <VerticalStack gap={8}>
        {error ? <Callout variant="error">{error}</Callout> : null}

        <section>
          <SectionHeading title="Code Details" subtitle="Define the code string, its status, active window, and redemption limits." />
          <Surface className="mt-4 border-border/60 p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Code">
                <input
                  value={form.code}
                  onChange={e => setForm(c => ({ ...c, code: e.target.value }))}
                  placeholder="e.g. SPRING2026"
                  className={inputClass}
                />
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={e => setForm(c => ({ ...c, status: e.target.value }))}
                  className={inputClass}
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </Field>
              <Field label="Starts At">
                <input
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={e => setForm(c => ({ ...c, startsAt: e.target.value }))}
                  className={inputClass}
                />
              </Field>
              <Field label="Ends At">
                <input
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={e => setForm(c => ({ ...c, endsAt: e.target.value }))}
                  className={inputClass}
                />
              </Field>
              <Field label="Use Limit" description="Leave blank for unlimited redemptions.">
                <input
                  type="number"
                  min="1"
                  value={form.useLimit}
                  onChange={e => setForm(c => ({ ...c, useLimit: e.target.value }))}
                  placeholder="Unlimited"
                  className={inputClass}
                />
              </Field>
              <Field label="Metadata JSON" className="sm:col-span-2">
                <textarea
                  value={form.metadataJSON}
                  onChange={e => setForm(c => ({ ...c, metadataJSON: e.target.value }))}
                  rows={5}
                  className="min-h-[120px] w-full rounded-lg border border-border/60 bg-background px-3 py-3 font-mono text-xs text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5"
                />
              </Field>
            </div>
          </Surface>
        </section>
      </VerticalStack>
    </WizardWorkspace>
  );
}
